// material/update — the side view's pure `update` (module contract) and its
// `inbound` (which Signals it listens to). Model × Msg → Step (next model,
// effects as data, Signals for other owners) or an explicit refusal when an
// author intent is not offered in the current state.
//
// Outcomes for a material that is no longer focused are ignored (a no-op Step,
// not a refusal: nothing was asked of the author).

import { exhaustSwitchCase, match, Result } from "@bilda/utils";
import type {
    GenerationJobSnapshot,
    GenerationSkeleton,
} from "../../app/ports.js";
import type { Inbound, Step, Update } from "../module.js";
import type { Signal } from "../signal.js";
import type { MaterialEffect } from "./effect.js";
import {
    type Answer,
    type Focus,
    failed,
    type JobIntent,
    loaded,
    loading,
    type MaterialModel,
    mkShown,
    noProblem,
    type Remote,
    type Shown,
} from "./model.js";
import type { MaterialMsg } from "./msg.js";
import { jobControls, partRows, snapshotOf } from "./view-model.js";

export type MaterialRefusal =
    | { readonly type: "material_not_shown" }
    | { readonly type: "generate_not_offered" }
    | { readonly type: "stop_not_offered" }
    | { readonly type: "continue_not_offered" }
    | { readonly type: "revision_not_offered"; readonly part: string }
    | { readonly type: "regenerate_not_offered" }
    | { readonly type: "still_fine_not_offered"; readonly part: string }
    | { readonly type: "version_answer_not_offered"; readonly part: string }
    | { readonly type: "approve_not_offered"; readonly part: string }
    | { readonly type: "revoke_not_offered"; readonly part: string };

export type MaterialStep = Step<MaterialModel, MaterialEffect>;

type Outcome = Result.Result<MaterialStep, MaterialRefusal>;

const step = (
    focus: Focus,
    effects: readonly MaterialEffect[] = [],
    signals: readonly Signal[] = [],
): Outcome => Result.ok({ model: { focus }, effects, signals });

const unchanged = (model: MaterialModel): Outcome =>
    Result.ok({ model, effects: [], signals: [] });

const refuse = (refusal: MaterialRefusal): Outcome => Result.err(refusal);

// Leaving a focus closes its live feed.
const leave = (focus: Focus): readonly MaterialEffect[] =>
    focus._tag === "Focus.Shown"
        ? [{ type: "unwatch_job", root: focus.root.id }]
        : [];

const firstLook = (root: string): readonly MaterialEffect[] => [
    { type: "load_skeleton", root },
    { type: "load_job", root },
];

const landedCount = (s: GenerationJobSnapshot | null): number =>
    s === null ? 0 : s.parts.filter((p) => p.state === "landed").length;

const reloadSkeleton = (root: string): MaterialEffect => ({
    type: "load_skeleton",
    root,
});

// A new snapshot on the shown focus. A part that newly landed changed the
// world (a committed version), so loaders re-read, the skeleton included:
// a part landed by an earlier job is not in this job's snapshot, so its row
// reads the skeleton, which must say "written" by now.
const withSnapshot = (
    shown: Shown,
    snapshot: GenerationJobSnapshot,
    patch: Partial<Settled> = {},
    effects: readonly MaterialEffect[] = [],
): Outcome => {
    const landed = landedCount(snapshot) > landedCount(snapshotOf(shown.feed));
    const next: Shown = {
        ...shown,
        ...patch,
        feed: { _tag: "JobFeed.Live", snapshot },
    };
    // An open part whose version moved (a revision landed) reads the new one.
    const opened = readOpened(next);
    return step(
        opened.shown,
        [
            ...effects,
            ...(landed ? [reloadSkeleton(shown.root.id)] : []),
            ...opened.effects,
        ],
        landed ? [{ _tag: "Signal.WorldChanged" }] : [],
    );
};

// Every open part whose content is readable but not read yet starts its read:
// a part opened before its row knew a version (a part the panel was asked to
// open at, bd-lya), or one whose version moved.
const readOpened = (
    shown: Shown,
): { readonly shown: Shown; readonly effects: readonly MaterialEffect[] } => {
    const unread = partRows(shown).flatMap((r) =>
        r.expanded && r.contentKey !== null && !shown.contents.has(r.contentKey)
            ? [{ part: r.id, key: r.contentKey }]
            : [],
    );
    return {
        shown: {
            ...shown,
            contents: unread.reduce(
                (m, u) => new Map(m).set(u.key, loading),
                shown.contents,
            ),
        },
        effects: unread.map(
            (u): MaterialEffect => ({
                type: "load_part_content",
                root: shown.root.id,
                part: u.part,
                key: u.key,
            }),
        ),
    };
};

// `step` for a shown focus, its open parts read.
const stepOpened = (shown: Shown): Outcome => {
    const opened = readOpened(shown);
    return step(opened.shown, opened.effects);
};

// A snapshot from a request/response (a job read, an intent's answer). The
// live feed publishes every change BEFORE the server answers the request that
// caused it, so once the feed is live an answer is never newer than what it
// already showed: it only settles the intent. Without a live feed it is the
// best news there is.
const withAnswer = (
    shown: Shown,
    snapshot: GenerationJobSnapshot,
    patch: Partial<Settled> = {},
    effects: readonly MaterialEffect[] = [],
): Outcome =>
    shown.feed._tag === "JobFeed.Live"
        ? step({ ...shown, ...patch }, effects)
        : withSnapshot(shown, snapshot, patch, effects);

const without = (set: ReadonlySet<string>, id: string): ReadonlySet<string> =>
    new Set([...set].filter((x) => x !== id));

// What answering `intent` settles: a job intent the pending control, a
// revision its part, a "still fine" its part.
type Settled = Pick<
    Shown,
    "pending" | "revising" | "stillFine" | "pinAnswers" | "reviewing"
>;

const settleIntent = (shown: Shown, intent: JobIntent): Settled => {
    const kept: Settled = {
        pending: shown.pending,
        revising: shown.revising,
        stillFine: shown.stillFine,
        pinAnswers: shown.pinAnswers,
        reviewing: shown.reviewing,
    };
    const review = ({ part }: { readonly part: string }): Settled => ({
        ...kept,
        reviewing: new Map([...shown.reviewing].filter(([p]) => p !== part)),
    });
    const job: Settled = { ...kept, pending: "idle" };
    return match<JobIntent, Settled>({
        "Intent.Start": () => job,
        "Intent.Stop": () => job,
        "Intent.Continue": () => job,
        "Intent.Revise": ({ part }) => ({
            ...kept,
            revising: without(shown.revising, part),
        }),
        "Intent.StillFine": ({ part }) => ({
            ...kept,
            stillFine: dropKey(shown.stillFine, part),
        }),
        "Intent.UseVersion": ({ part }) => ({
            ...kept,
            pinAnswers: dropKey(shown.pinAnswers, part),
        }),
        "Intent.KeepVersion": ({ part }) => ({
            ...kept,
            pinAnswers: dropKey(shown.pinAnswers, part),
        }),
        "Intent.Approve": review,
        "Intent.RevokeApproval": review,
    })(intent);
};

// A fresh skeleton read (or a failed one) settles every accepted answer
// ("still fine", use / keep a version): the read is what says how the part
// stands now.
const settleSent = (
    answers: ReadonlyMap<string, Answer>,
): ReadonlyMap<string, Answer> =>
    new Map([...answers].filter(([, s]) => s === "sending"));

const dropKey = <V>(
    map: ReadonlyMap<string, V>,
    key: string,
): ReadonlyMap<string, V> => new Map([...map].filter(([k]) => k !== key));

// The focus for `root`'s outcome, or undefined when it no longer applies.
const shownFor = (focus: Focus, root: string): Shown | undefined =>
    focus._tag === "Focus.Shown" && focus.root.id === root ? focus : undefined;
const onSkeleton = (
    model: MaterialModel,
    root: string,
    skeleton: Remote<GenerationSkeleton>,
): Outcome => {
    const shown = shownFor(model.focus, root);
    if (shown === undefined) return unchanged(model);
    const settled = {
        stillFine: settleSent(shown.stillFine),
        pinAnswers: settleSent(shown.pinAnswers),
        reviewing: new Map(
            [...shown.reviewing].filter(([, r]) => r.phase === "sending"),
        ),
    };
    // A failed RE-read keeps the skeleton already shown.
    return skeleton._tag === "Remote.Failed" &&
        shown.skeleton._tag === "Remote.Loaded"
        ? step({ ...shown, ...settled })
        : stepOpened({ ...shown, skeleton, ...settled });
};

const onJobRead = (
    model: MaterialModel,
    root: string,
    job: Remote<GenerationJobSnapshot | null>,
): Outcome => {
    const shown = shownFor(model.focus, root);
    if (shown === undefined) return unchanged(model);
    const wasLost = shown.feed._tag === "JobFeed.Lost";
    // After a drop, make sure the feed is open again (idempotent).
    const reopen: readonly MaterialEffect[] = wasLost
        ? [{ type: "watch_job", root }]
        : [];
    return match<Remote<GenerationJobSnapshot | null>, Outcome>({
        "Remote.Loading": () => unchanged(model),
        // Unreachable: keep what is shown; a loading feed reads as lost.
        "Remote.Failed": () =>
            shown.feed._tag === "JobFeed.Loading"
                ? step({ ...shown, feed: { _tag: "JobFeed.Lost", last: null } })
                : unchanged(model),
        "Remote.Loaded": ({ value }) =>
            value !== null
                ? withAnswer(shown, value, {}, reopen)
                : shown.feed._tag === "JobFeed.Live"
                  ? unchanged(model)
                  : step({ ...shown, feed: { _tag: "JobFeed.NoJob" } }, reopen),
    })(job);
};

// The notice to answer on `part`'s row, while it is offered (shown, and not
// answered yet).
const noticeOn = (s: Shown, part: string) =>
    partRows(s).find((r) => r.id === part)?.newerVersions ?? null;

// An answer to a newer-versions notice: hidden at once (optimistic), the
// effect carries the include it answers.
const answerNotice = (
    model: MaterialModel,
    part: string,
    offered: (heads: readonly number[]) => boolean,
    effect: (root: string, refId: string) => MaterialEffect,
): Outcome => {
    const focus = model.focus;
    if (focus._tag !== "Focus.Shown")
        return refuse({ type: "material_not_shown" });
    const notice = noticeOn(focus, part);
    return notice === null || !offered(notice.heads)
        ? refuse({ type: "version_answer_not_offered", part })
        : step(
              {
                  ...focus,
                  pinAnswers: new Map(focus.pinAnswers).set(part, "sending"),
                  problem: noProblem,
              },
              [effect(focus.root.id, notice.refId)],
          );
};

const intent = (
    model: MaterialModel,
    when: (shown: Shown) => boolean,
    refusal: MaterialRefusal,
    next: (shown: Shown) => Outcome,
): Outcome => {
    const focus = model.focus;
    if (focus._tag !== "Focus.Shown")
        return refuse({ type: "material_not_shown" });
    return when(focus) ? next(focus) : refuse(refusal);
};

export const updateMaterial: Update<
    MaterialModel,
    MaterialMsg,
    MaterialEffect,
    MaterialRefusal
> = (model, msg) => {
    const focus = model.focus;
    switch (msg.type) {
        case "material_focused": {
            // Asked to open at a part (a course's module): it opens as soon
            // as its row can be read.
            const opening = msg.part === undefined ? [] : [msg.part];
            if (focus._tag === "Focus.Shown" && focus.root.id === msg.root.id)
                return opening.length === 0
                    ? unchanged(model)
                    : stepOpened({
                          ...focus,
                          expanded: new Set([...focus.expanded, ...opening]),
                      });
            return step(
                {
                    ...mkShown(msg.root, loading, { _tag: "JobFeed.Loading" }),
                    expanded: new Set(opening),
                },
                [
                    ...leave(focus),
                    ...firstLook(msg.root.id),
                    { type: "watch_job", root: msg.root.id },
                ],
            );
        }
        // Re-read what a reload reads (skeleton + job): a turn's tools may
        // have started a job or written parts while the feed had nothing to
        // say (no job yet), so the panel converges on the durable truth.
        case "world_changed":
            return focus._tag === "Focus.Shown"
                ? step(focus, firstLook(focus.root.id))
                : unchanged(model);
        case "material_unfocused":
            return focus._tag === "Focus.Shown"
                ? step({ _tag: "Focus.None" }, leave(focus))
                : unchanged(model);
        case "generate_requested":
            return intent(
                model,
                (s) => {
                    const c = jobControls(s);
                    return c._tag === "Controls.Generate" && !c.pending;
                },
                { type: "generate_not_offered" },
                (s) =>
                    step({ ...s, pending: "starting", problem: noProblem }, [
                        { type: "start_job", root: s.root.id },
                    ]),
            );
        case "stop_requested":
            return intent(
                model,
                (s) => {
                    const c = jobControls(s);
                    return (
                        (c._tag === "Controls.Running" && !c.stopping) ||
                        c._tag === "Controls.Paused"
                    );
                },
                { type: "stop_not_offered" },
                (s) =>
                    step({ ...s, pending: "stopping", problem: noProblem }, [
                        { type: "stop_job", root: s.root.id },
                    ]),
            );
        case "continue_requested":
            return intent(
                model,
                (s) => {
                    const c = jobControls(s);
                    return (
                        (c._tag === "Controls.Paused" ||
                            c._tag === "Controls.Interrupted") &&
                        !c.pending
                    );
                },
                { type: "continue_not_offered" },
                (s) =>
                    step({ ...s, pending: "continuing", problem: noProblem }, [
                        { type: "continue_job", root: s.root.id },
                    ]),
            );
        case "revision_requested": {
            const feedback = msg.feedback.trim();
            return intent(
                model,
                (s) =>
                    feedback.length > 0 &&
                    !s.revising.has(msg.part) &&
                    partRows(s).some((r) => r.id === msg.part && r.canRevise),
                { type: "revision_not_offered", part: msg.part },
                (s) =>
                    step(
                        {
                            ...s,
                            revising: new Set([...s.revising, msg.part]),
                            problem: noProblem,
                        },
                        [
                            {
                                type: "revise_part",
                                root: s.root.id,
                                part: msg.part,
                                feedback,
                            },
                        ],
                    ),
            );
        }
        case "regenerate_requested": {
            const parts = msg.parts;
            return intent(
                model,
                (s) => {
                    const rows = partRows(s);
                    return (
                        parts.length > 0 &&
                        parts.every((p) =>
                            rows.some((r) => r.id === p && r.canRegenerate),
                        )
                    );
                },
                { type: "regenerate_not_offered" },
                (s) =>
                    step({ ...s, pending: "starting", problem: noProblem }, [
                        { type: "start_job", root: s.root.id, parts },
                    ]),
            );
        }
        case "still_fine_requested": {
            const row = (s: Shown) =>
                partRows(s).find((r) => r.id === msg.part);
            return intent(
                model,
                (s) => (row(s)?.stale.length ?? 0) > 0,
                { type: "still_fine_not_offered", part: msg.part },
                (s) =>
                    step(
                        {
                            ...s,
                            stillFine: new Map(s.stillFine).set(
                                msg.part,
                                "sending",
                            ),
                            problem: noProblem,
                        },
                        [
                            {
                                type: "mark_still_fine",
                                root: s.root.id,
                                part: msg.part,
                                dependencies: (row(s)?.stale ?? []).map(
                                    (r) => r.dependency,
                                ),
                            },
                        ],
                    ),
            );
        }
        case "newer_version_chosen":
            return answerNotice(
                model,
                msg.part,
                (heads) => heads.includes(msg.version),
                (root, refId) => ({
                    type: "use_newer_version",
                    root,
                    part: msg.part,
                    refId,
                    toVersion: msg.version,
                }),
            );
        case "version_kept":
            return answerNotice(
                model,
                msg.part,
                () => true,
                (root, refId) => ({
                    type: "keep_version",
                    root,
                    part: msg.part,
                    refId,
                }),
            );
        case "approve_requested":
        case "approval_revoke_requested": {
            if (focus._tag !== "Focus.Shown")
                return refuse({ type: "material_not_shown" });
            const approve = msg.type === "approve_requested";
            const row = partRows(focus).find((r) => r.id === msg.part);
            const version = approve ? row?.approveVersion : row?.revokeVersion;
            if (version === undefined || version === null)
                return refuse(
                    approve
                        ? { type: "approve_not_offered", part: msg.part }
                        : { type: "revoke_not_offered", part: msg.part },
                );
            return step(
                {
                    ...focus,
                    reviewing: new Map(focus.reviewing).set(msg.part, {
                        intent: approve
                            ? "Intent.Approve"
                            : "Intent.RevokeApproval",
                        phase: "sending",
                    }),
                    problem: noProblem,
                },
                [
                    {
                        type: approve ? "approve_part" : "revoke_part_approval",
                        root: focus.root.id,
                        part: msg.part,
                        version,
                    },
                ],
            );
        }
        case "review_answered": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined
                ? unchanged(model)
                : step(
                      {
                          ...shown,
                          reviewing: new Map(shown.reviewing).set(
                              msg.intent.part,
                              { intent: msg.intent._tag, phase: "sent" },
                          ),
                      },
                      [reloadSkeleton(shown.root.id)],
                      [{ _tag: "Signal.WorldChanged" }],
                  );
        }
        case "review_failed": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined
                ? unchanged(model)
                : step({
                      ...shown,
                      ...settleIntent(shown, msg.intent),
                      problem: {
                          _tag: "Problem.IntentFailed",
                          intent: msg.intent,
                      },
                  });
        }
        // Every open is a `show-in-panel` (the frame owner's).
        case "dependency_opened":
            return Result.ok({
                model,
                effects: [],
                signals: [
                    {
                        _tag: "Signal.FrameRequested",
                        transition: { type: "show-in-panel", ref: msg.ref },
                    },
                ],
            });
        case "still_fine_answered": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined
                ? unchanged(model)
                : step(
                      {
                          ...shown,
                          stillFine: new Map(shown.stillFine).set(
                              msg.part,
                              "sent",
                          ),
                      },
                      [reloadSkeleton(shown.root.id)],
                      [{ _tag: "Signal.WorldChanged" }],
                  );
        }
        case "still_fine_failed": {
            const shown = shownFor(focus, msg.root);
            const failedIntent: JobIntent = {
                _tag: "Intent.StillFine",
                part: msg.part,
            };
            return shown === undefined
                ? unchanged(model)
                : step({
                      ...shown,
                      ...settleIntent(shown, failedIntent),
                      problem: {
                          _tag: "Problem.IntentFailed",
                          intent: failedIntent,
                      },
                  });
        }
        case "pin_answered": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined
                ? unchanged(model)
                : step(
                      {
                          ...shown,
                          pinAnswers: new Map(shown.pinAnswers).set(
                              msg.part,
                              "sent",
                          ),
                      },
                      [reloadSkeleton(shown.root.id)],
                      [{ _tag: "Signal.WorldChanged" }],
                  );
        }
        case "pin_answer_failed": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined
                ? unchanged(model)
                : step({
                      ...shown,
                      ...settleIntent(shown, msg.intent),
                      problem: {
                          _tag: "Problem.IntentFailed",
                          intent: msg.intent,
                      },
                  });
        }
        case "part_opened": {
            if (focus._tag !== "Focus.Shown") return unchanged(model);
            const expanded = new Set([...focus.expanded, msg.part]);
            const have = focus.contents.get(msg.key);
            if (have !== undefined && have._tag !== "Remote.Failed")
                return step({ ...focus, expanded });
            return step(
                {
                    ...focus,
                    expanded,
                    contents: new Map(focus.contents).set(msg.key, loading),
                },
                [
                    {
                        type: "load_part_content",
                        root: focus.root.id,
                        part: msg.part,
                        key: msg.key,
                    },
                ],
            );
        }
        case "part_closed":
            return focus._tag === "Focus.Shown"
                ? step({
                      ...focus,
                      expanded: without(focus.expanded, msg.part),
                  })
                : unchanged(model);
        case "skeleton_loaded":
            return onSkeleton(model, msg.root, loaded(msg.skeleton));
        case "skeleton_failed":
            return onSkeleton(model, msg.root, failed);
        case "job_loaded":
            return onJobRead(model, msg.root, loaded(msg.snapshot));
        case "job_failed":
            return onJobRead(model, msg.root, failed);
        case "feed_snapshot": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined
                ? unchanged(model)
                : withSnapshot(shown, msg.snapshot);
        }
        case "feed_lost": {
            const shown = shownFor(focus, msg.root);
            if (shown === undefined || shown.feed._tag === "JobFeed.Lost")
                return unchanged(model);
            return step(
                {
                    ...shown,
                    feed: {
                        _tag: "JobFeed.Lost",
                        last: snapshotOf(shown.feed),
                    },
                },
                [{ type: "load_job", root: msg.root }],
            );
        }
        case "feed_restored": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined || shown.feed._tag !== "JobFeed.Lost"
                ? unchanged(model)
                : step(shown, [{ type: "load_job", root: msg.root }]);
        }
        case "intent_answered": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined
                ? unchanged(model)
                : withAnswer(
                      shown,
                      msg.snapshot,
                      settleIntent(shown, msg.intent),
                  );
        }
        case "intent_failed": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined
                ? unchanged(model)
                : step({
                      ...shown,
                      ...settleIntent(shown, msg.intent),
                      problem: {
                          _tag: "Problem.IntentFailed",
                          intent: msg.intent,
                      },
                  });
        }
        case "start_refused_ungrounded": {
            const shown = shownFor(focus, msg.root);
            return shown === undefined
                ? unchanged(model)
                : step({
                      ...shown,
                      ...settleIntent(shown, { _tag: "Intent.Start" }),
                      problem: { _tag: "Problem.Ungrounded" },
                  });
        }
        case "part_content_loaded":
        case "part_content_failed": {
            const shown = shownFor(focus, msg.root);
            if (shown === undefined) return unchanged(model);
            const value =
                msg.type === "part_content_loaded"
                    ? loaded(msg.content)
                    : failed;
            return step({
                ...shown,
                contents: new Map(shown.contents).set(msg.key, value),
            });
        }
        default:
            return exhaustSwitchCase(msg);
    }
};

// A material is shown by the right panel's focus (every open is a
// `show-in-panel`), which the mount reports as `material_focused`; the world
// changing re-reads the shown one.
export const inboundMaterial: Inbound<MaterialMsg> = match<
    Signal,
    MaterialMsg | undefined
>({
    "Signal.NoticeRaised": () => undefined,
    "Signal.WorldChanged": () => ({ type: "world_changed" }),
    "Signal.FrameRequested": () => undefined,
    "Signal.ConversationStarted": () => undefined,
    "Signal.PendingGroundingFlushed": () => undefined,
    "Signal.ProposalLanded": () => undefined,
    "Signal.StopRequested": () => undefined,
});
